import React from "react";
import PropTypes from "prop-types";

const cleanText = (value = "") => String(value || "").trim();

const uniqueList = (items = []) => {
  const seen = new Set();

  return items
    .map((item) => cleanText(item))
    .filter(Boolean)
    .filter((item) => {
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getVerifyTitle = ({ verifyingHosting, verifyCooldown }) => {
  if (verifyingHosting) return "Checking DNS...";
  if (verifyCooldown > 0) return `Auto checking in ${verifyCooldown}s`;
  return "Check Live Status";
};

function HostingDnsConnectPanel({
  selectedDomain,
  dnsSetup,
  connectingHosting,
  verifyingHosting,
  hostingVerifyResult,
  platformNs1,
  platformNs2,
  platformNs3,
  platformNs4,
  platformNameservers,
  hostingAIp,
  backendHostname,
  onConnectHosting,
  onVerifyHosting,
  verifyCooldown,
}) {
  if (!selectedDomain) return null;

  const nameservers = uniqueList([
    ...(Array.isArray(platformNameservers) ? platformNameservers : []),
    platformNs1,
    platformNs2,
    platformNs3,
    platformNs4,
  ]);

  const isVerified = Boolean(hostingVerifyResult?.verified);
  const checks = hostingVerifyResult?.checks || null;

  return (
    <div className={`dns-hosting-status ${dnsSetup.status}`}>
      <div>
        <strong>{dnsSetup.label}</strong>
        <span>{dnsSetup.message}</span>
      </div>

      <div className="dns-example-box">
        <strong>Required setup for live website</strong>

        {nameservers.length > 0 ? (
          nameservers.map((ns, index) => (
            <span key={`${ns}-${index}`}>
              Nameserver {index + 1}: {ns}
            </span>
          ))
        ) : (
          <span>Nameservers will be loaded from DoHostingPay DNS.</span>
        )}

        <span>A Record: @ → {hostingAIp}</span>
        <span>CNAME: www → {backendHostname}</span>
        <span>Recommended TTL: 300 seconds for faster activation</span>
      </div>

      <div className="dns-example-box">
        <strong>
          {isVerified
            ? "✅ Custom domain live"
            : "🌍 Custom domain is connecting globally"}
        </strong>

        <span>
          {isVerified
            ? "Your website is now live on your custom domain."
            : "Your preview website is already live. Custom domain activation continues automatically in the background."}
        </span>

        {hostingVerifyResult?.message ? (
          <span>{hostingVerifyResult.message}</span>
        ) : null}

        {checks ? (
          <>
            <span>
              Nameserver: {checks.nameserverVerified ? "Verified" : "Checking"}
            </span>
            <span>
              Root A Record: {checks.rootVerified ? "Verified" : "Checking"}
            </span>
            <span>WWW Record: {checks.wwwVerified ? "Verified" : "Checking"}</span>
          </>
        ) : (
          <>
            <span>Records saved: Ready</span>
            <span>Preview website: Live</span>
            <span>Live domain: Auto checking</span>
          </>
        )}
      </div>

      <div className="dns-record-actions" style={{ marginTop: "14px" }}>
        <button
          type="button"
          className="dns-btn"
          disabled={connectingHosting}
          onClick={onConnectHosting}
        >
          {connectingHosting ? "Connecting..." : "Connect Hosting DNS"}
        </button>

        <button
          type="button"
          className="dns-btn"
          disabled={verifyingHosting || verifyCooldown > 0}
          onClick={onVerifyHosting}
        >
          {getVerifyTitle({ verifyingHosting, verifyCooldown })}
        </button>
      </div>
    </div>
  );
}

HostingDnsConnectPanel.propTypes = {
  selectedDomain: PropTypes.string,
  dnsSetup: PropTypes.shape({
    status: PropTypes.string,
    label: PropTypes.string,
    message: PropTypes.string,
  }),
  connectingHosting: PropTypes.bool,
  verifyingHosting: PropTypes.bool,
  hostingVerifyResult: PropTypes.shape({
    verified: PropTypes.bool,
    message: PropTypes.string,
    checks: PropTypes.shape({
      nameserverVerified: PropTypes.bool,
      rootVerified: PropTypes.bool,
      wwwVerified: PropTypes.bool,
    }),
  }),
  platformNs1: PropTypes.string,
  platformNs2: PropTypes.string,
  platformNs3: PropTypes.string,
  platformNs4: PropTypes.string,
  platformNameservers: PropTypes.arrayOf(PropTypes.string),
  hostingAIp: PropTypes.string,
  backendHostname: PropTypes.string,
  onConnectHosting: PropTypes.func,
  onVerifyHosting: PropTypes.func,
  verifyCooldown: PropTypes.number,
};

HostingDnsConnectPanel.defaultProps = {
  selectedDomain: "",
  dnsSetup: {
    status: "pending",
    label: "Website DNS Setup Required",
    message: "Connect hosting DNS to make this domain point to your website.",
  },
  connectingHosting: false,
  verifyingHosting: false,
  hostingVerifyResult: null,
  platformNs1: "",
  platformNs2: "",
  platformNs3: "",
  platformNs4: "",
  platformNameservers: [],
  hostingAIp: "",
  backendHostname: "",
  onConnectHosting: () => {},
  onVerifyHosting: () => {},
  verifyCooldown: 0,
};

export default React.memo(HostingDnsConnectPanel);